import { request } from '@umijs/max';
import { registerWsHandler, WsEventType } from '../websocket';

/** A pending confirmation prompt the CSR must acknowledge. */
export type ActivityPrompt = {
  id: number;
  /** 'random' draw during activity, 'inactivity' after 15 idle minutes, or 'manual' — sent by a manager with their own message. */
  reason: 'random' | 'inactivity' | 'manual';
  /** Custom text for a manager-sent ('manual') prompt; null otherwise. */
  message: string | null;
  /** Server-computed countdown, immune to client clock skew. */
  seconds_remaining: number;
  response_window_seconds: number;
};

export type ActivityStatus = {
  tracking_enabled: boolean;
  heartbeat_interval?: number;
  prompt?: ActivityPrompt | null;
};

export type ActivityHeartbeatResult = ActivityStatus & { recorded?: boolean };

/** Fired when a manager-sent "csr-prompt-issued" WS push arrives (see registerWsHandler call below). */
export const CSR_PROMPT_ISSUED_EVENT = 'app:csr-prompt-issued';

/**
 * `data` carries the prompt body straight off the WS push (see
 * promptPayload() on the backend) so a listener can show it directly instead
 * of turning around and calling getActivityStatus() over REST. Undefined
 * when the push arrived without a body (older server, or a plain
 * emitCsrPromptIssued() call) — callers should fall back to a REST fetch.
 */
export function emitCsrPromptIssued(data?: ActivityPrompt) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<ActivityPrompt | undefined>(CSR_PROMPT_ISSUED_EVENT, { detail: data }));
  }
}

export function onCsrPromptIssued(handler: (data?: ActivityPrompt) => void) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const listener = (e: Event) => handler((e as CustomEvent<ActivityPrompt | undefined>).detail);
  window.addEventListener(CSR_PROMPT_ISSUED_EVENT, listener);
  return () => window.removeEventListener(CSR_PROMPT_ISSUED_EVENT, listener);
}

registerWsHandler(WsEventType.CSR_PROMPT_ISSUED, emitCsrPromptIssued);

/**
 * Whether activity tracking applies to the current user (CSR role) and any
 * prompt still pending from before a reload.
 */
export async function getActivityStatus(): Promise<ActivityStatus> {
  return request<{ success: boolean; data: ActivityStatus }>(
    '/api/tenant/csr/activity/status',
    { method: 'GET', withToken: true },
  ).then((res) => res.data);
}

/**
 * Report one interval of in-app activity. Counters must only include
 * keyboard/mouse events observed while the app tab was visible and focused.
 */
export async function sendActivityHeartbeat(data: {
  interval_seconds: number;
  active_seconds: number;
  keyboard_events: number;
  mouse_events: number;
}): Promise<ActivityHeartbeatResult> {
  return request<{ success: boolean; data: ActivityHeartbeatResult }>(
    '/api/tenant/csr/activity/heartbeat',
    { method: 'POST', data, withToken: true },
  ).then((res) => res.data);
}

/**
 * Acknowledge a confirmation prompt (random or inactivity).
 */
export async function confirmActivityPrompt(promptId: number): Promise<{
  prompt_id: number;
  status: 'pending' | 'confirmed' | 'missed';
}> {
  return request<{ success: boolean; data: { prompt_id: number; status: 'pending' | 'confirmed' | 'missed' } }>(
    '/api/tenant/csr/activity/prompt/confirm',
    { method: 'POST', data: { prompt_id: promptId }, withToken: true },
  ).then((res) => res.data);
}
